import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, MenuItem, Paper,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useNotify } from "react-admin";
import { CLO_NETWORKS, cloError, cloGet, cloPatch, cloPost, ENDPOINTS, formatDate, percent, pounds } from "./api";
import VersionEditor from "./VersionEditor";
import { fromVersion, toVersionBody, VersionForm } from "./versionForm";
import { statusChip } from "./CampaignsTab";

interface Props {
  id: number;
  onClose: () => void;
}

const params = (value: any) => JSON.stringify(value);

const CampaignDetail: React.FC<Props> = ({ id, onClose }) => {
  const notify = useNotify();
  const [campaign, setCampaign] = useState<any | null>(null);
  const [editing, setEditing] = useState<VersionForm | null>(null);
  const [review, setReview] = useState<{ version: any; action: "approve" | "reject" } | null>(null);
  const [comment, setComment] = useState("");
  const [override, setOverride] = useState("");
  const [pauseReason, setPauseReason] = useState("");
  const [offer, setOffer] = useState({ network: "visa", offerId: "" });
  const [header, setHeader] = useState<any | null>(null);
  const [check, setCheck] = useState<{ errors: string[]; warnings: string[] } | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await cloGet(`${ENDPOINTS.CLO_CAMPAIGNS}/${id}`);
      setCampaign(res.data);
    } catch (e) {
      notify(cloError(e), "warning");
    }
  }, [id, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const action = async (body: any, message: string) => {
    try {
      const res = await cloPost(ENDPOINTS.CLO_CAMPAIGN_ACTIONS, { campaignId: id, ...body });
      setCampaign(res.data);
      notify(message, "info");
      return true;
    } catch (e) {
      notify(cloError(e), "warning");
      return false;
    }
  };

  const saveDraft = async () => {
    if (!editing) return;
    let version;
    try {
      version = toVersionBody(editing);
    } catch (e: any) {
      notify(e.message, "warning");
      return;
    }
    if (await action({ action: "save_draft", version }, "Draft saved")) setEditing(null);
  };

  const saveHeader = async () => {
    try {
      const res = await cloPatch(`${ENDPOINTS.CLO_CAMPAIGNS}/${id}`, {
        name: header.name,
        partnerCampaignRef: header.partnerCampaignRef.trim() || null,
      });
      setCampaign(res.data);
      setHeader(null);
      notify("Campaign updated", "info");
    } catch (e) {
      notify(cloError(e), "warning");
    }
  };

  const openApprove = async (version: any) => {
    setCheck(null);
    setReview({ version, action: "approve" });
    try {
      const res = await cloPost(ENDPOINTS.CLO_CAMPAIGN_ACTIONS, { campaignId: id, action: "approval_check", versionId: version.id });
      setCheck(res.data);
    } catch (e) {
      setCheck({ errors: [], warnings: [`Could not run the checks: ${cloError(e)}`] });
    }
  };

  const submitReview = async () => {
    if (!review) return;
    const ok = await action(
      review.action === "approve"
        ? { action: "approve", versionId: review.version.id, comment: comment || null, midOverrideReason: override || null }
        : { action: "reject", versionId: review.version.id, comment },
      review.action === "approve" ? "Version approved and published" : "Version rejected"
    );
    if (ok) {
      setReview(null);
      setComment("");
      setOverride("");
    }
  };

  if (!campaign) return null;

  const versions: any[] = campaign.versions || [];
  const openVersion = versions.find((v) => v.status === "draft" || v.status === "rejected");
  const awaiting = versions.find((v) => v.status === "awaiting_approval");
  const latestApproved = versions.find((v) => v.status === "approved");
  const openPause = (campaign.pauses || []).find((p: any) => !p.resumed_at);
  const liveVersion = versions.find((v) => Number(v.version) === Number(campaign.current_version));
  const latestDates = latestApproved && { version: latestApproved.version, start_date: latestApproved.start_date, end_date: latestApproved.end_date };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle disableTypography>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {campaign.name} · {campaign.retailer_name} {statusChip(campaign.status)}
          </Typography>
          <IconButton aria-label="Close" size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" style={{ padding: 12 }}>
              <Typography variant="subtitle2">Campaign</Typography>
              <Typography variant="body2">Dates: {campaign.start_date ? `${campaign.start_date} → ${campaign.end_date}` : "—"} (London time)</Typography>
              <Typography variant="body2">Pricing right now: {campaign.pricing_status_now}</Typography>
              <Typography variant="body2">Live version: {campaign.current_version ? `v${campaign.current_version}` : "none yet"}</Typography>
              <Typography variant="body2">Cohort rules version: {liveVersion?.cohort_definition_version ?? "—"}</Typography>
              <Typography variant="body2">Pending period: {campaign.pending_period_days} days</Typography>
              <Typography variant="body2">Partner campaign ID: {campaign.partner_campaign_ref || "—"}</Typography>
              <Button size="small" onClick={() => setHeader({ name: campaign.name, partnerCampaignRef: campaign.partner_campaign_ref || "" })}>Edit</Button>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" style={{ padding: 12 }}>
              <Typography variant="subtitle2">Pause / resume</Typography>
              {campaign.pricing_status_now === "live" && campaign.current_version && (
                <Box display="flex" style={{ gap: 8 }}>
                  <TextField size="small" label="Reason" value={pauseReason} onChange={(e) => setPauseReason(e.target.value)} />
                  <Button color="secondary" variant="outlined" onClick={() => action({ action: "pause", reason: pauseReason || null }, "Campaign paused")}>Pause now</Button>
                </Box>
              )}
              {openPause && (
                <Box>
                  <Typography variant="body2">Paused {formatDate(openPause.paused_at)} by {openPause.paused_by}{openPause.reason ? ` · ${openPause.reason}` : ""}</Typography>
                  {!openPause.resume_requested_by && (
                    <Button size="small" color="primary" onClick={() => action({ action: "request_resume" }, "Resume requested")}>Ask to resume</Button>
                  )}
                  {openPause.resume_requested_by && (
                    <Typography variant="body2">Resume asked by {openPause.resume_requested_by}</Typography>
                  )}
                  {campaign.can_approve_resume && (
                    <Button size="small" color="primary" variant="contained" onClick={() => action({ action: "approve_resume" }, "Campaign resumed")}>Approve resume</Button>
                  )}
                </Box>
              )}
              {!openPause && campaign.pricing_status_now !== "live" && (
                <Typography variant="body2" color="textSecondary">Only a live campaign can be paused.</Typography>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" style={{ padding: 12 }}>
              <Typography variant="subtitle2">Network offer IDs</Typography>
              <Typography variant="caption" color="textSecondary">Visa sends this ID on every purchase; matching uses it to find the campaign.</Typography>
              {(campaign.offers || []).map((o: any) => (
                <Box key={o.id} display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2">{o.network}: {o.offer_id}</Typography>
                  <Button size="small" onClick={() => action({ action: "remove_offer", offerRowId: o.id }, "Offer removed")}>Remove</Button>
                </Box>
              ))}
              <Box display="flex" style={{ gap: 8 }} mt={1}>
                <TextField select size="small" value={offer.network} onChange={(e) => setOffer({ ...offer, network: e.target.value })}>
                  {CLO_NETWORKS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                </TextField>
                <TextField size="small" label="Offer ID" value={offer.offerId} onChange={(e) => setOffer({ ...offer, offerId: e.target.value })} />
                <Button size="small" disabled={!offer.offerId} onClick={async () => { if (await action({ action: "add_offer", ...offer }, "Offer linked")) setOffer({ ...offer, offerId: "" }); }}>Add</Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Box mt={3} display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Versions</Typography>
          {!openVersion && !awaiting && latestApproved && campaign.pricing_status_now !== "ended" && (
            <Button variant="outlined" color="primary" onClick={() => setEditing({ ...fromVersion(latestApproved), startDate: "" })}>
              Change pricing or dates (new version from v{latestApproved.version})
            </Button>
          )}
        </Box>
        {campaign.pricing_status_now === "ended" && (
          <Typography variant="body2" color="textSecondary">This campaign has ended, so it can no longer change. Create a new campaign to run longer.</Typography>
        )}
        {versions.map((v) => (
          <Paper key={v.id} variant="outlined" style={{ padding: 12, marginTop: 12 }}>
            <Box display="flex" alignItems="center" style={{ gap: 8 }} flexWrap="wrap">
              <Typography variant="subtitle1">v{v.version}</Typography>
              <Chip size="small" label={v.status} color={v.status === "approved" ? "primary" : v.status === "awaiting_approval" ? "secondary" : "default"} />
              {v.maker && <Typography variant="caption">Submitted by {v.maker} {formatDate(v.submitted_at)}</Typography>}
              {v.approver && <Typography variant="caption">· Approved by {v.approver} {formatDate(v.approved_at)}</Typography>}
              {v.review_comment && <Typography variant="caption">· Comment: {v.review_comment}</Typography>}
              {v.mid_override_reason && <Typography variant="caption" color="error">· MID override: {v.mid_override_reason}</Typography>}
              <Box flexGrow={1} />
              {(v.status === "draft" || v.status === "rejected") && (
                <>
                  <Button size="small" onClick={() => setEditing(fromVersion(v))}>Edit</Button>
                  {v.status === "draft" && (
                    <Button size="small" color="primary" variant="contained" disabled={v.errors.length > 0} onClick={() => action({ action: "submit", versionId: v.id }, "Submitted for approval")}>
                      Submit for approval
                    </Button>
                  )}
                </>
              )}
              {v.status === "awaiting_approval" && (
                v.can_approve ? (
                  <>
                    <Button size="small" color="primary" variant="contained" onClick={() => openApprove(v)}>Approve</Button>
                    <Button size="small" color="secondary" onClick={() => setReview({ version: v, action: "reject" })}>Reject</Button>
                  </>
                ) : (
                  <Typography variant="caption" color="textSecondary">You submitted this version, so someone else must approve it.</Typography>
                )
              )}
            </Box>
            <Typography variant="body2">
              Dates: {v.start_date} → {v.end_date} · Cohort rules: {v.cohort_definition_version ?? "—"} ·
              Networks: {(v.networks || []).join(", ")} · Budget cap: {pounds(v.campaign_cap_minor)} · VAT: {percent(v.vat_rate_bp)} ·
              Activation: {Number(v.activation_required) ? "required" : "not required"} · After one-time: Existing · Cap split: {v.cap_split}
              {v.pending_period_days !== null && v.pending_period_days !== undefined ? ` · Pending period: ${v.pending_period_days} days` : ""}
            </Typography>
            {v.errors.length > 0 && v.status !== "approved" && v.errors.map((e: string) => <Typography key={e} variant="caption" display="block" color="error">✗ {e}</Typography>)}
            {v.warnings.length > 0 && v.status !== "approved" && v.warnings.map((w: string) => <Typography key={w} variant="caption" display="block">⚠ {w}</Typography>)}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Cohort</TableCell>
                  <TableCell>Reward rule</TableCell>
                  <TableCell>Fee</TableCell>
                  <TableCell>One-time</TableCell>
                  <TableCell>Min spend</TableCell>
                  <TableCell>Hold-out</TableCell>
                  <TableCell>Cap / purchase</TableCell>
                  <TableCell>Cohort cap</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(v.cohorts || []).map((c: any) => (
                  <TableRow key={c.cohort_name}>
                    <TableCell>{c.cohort_name}</TableCell>
                    <TableCell><Typography variant="caption">{c.ruleType} {params(c.ruleParams)}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{c.feeType} {params(c.feeParams)}</Typography></TableCell>
                    <TableCell>{c.oneTime ? "yes" : "no"}</TableCell>
                    <TableCell>{pounds(c.minSpendMinor)}</TableCell>
                    <TableCell>{percent(c.holdoutShareBp)}</TableCell>
                    <TableCell>{pounds(c.transactionCapMinor)}</TableCell>
                    <TableCell>{pounds(c.cohortCapMinor)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <Dialog disableBackdropClick open={editing !== null} onClose={() => setEditing(null)} maxWidth="lg" fullWidth>
        <DialogTitle>Edit draft version</DialogTitle>
        <DialogContent>
          {editing && (
            <VersionEditor
              value={editing}
              onChange={setEditing}
              liveVersion={latestDates || undefined}
              campaignId={id}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={saveDraft}>Save draft</Button>
        </DialogActions>
      </Dialog>

      <Dialog disableBackdropClick open={review !== null} onClose={() => setReview(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{review?.action === "approve" ? `Approve v${review?.version.version}` : `Reject v${review?.version.version}`}</DialogTitle>
        <DialogContent>
          {review?.action === "approve" && !check && (
            <Typography variant="body2" color="textSecondary" gutterBottom>Checking cohort rules and control group sizes…</Typography>
          )}
          {review?.action === "approve" && (check?.errors || []).map((e: string) => (
            <Typography key={e} variant="body2" color="error" gutterBottom>✗ {e}</Typography>
          ))}
          {review?.action === "approve" && [...(review.version.warnings || []), ...(check?.warnings || [])].map((w: string) => (
            <Typography key={w} variant="body2" style={{ color: "#b26a00" }} gutterBottom>⚠ {w}</Typography>
          ))}
          {review?.action === "approve" && latestApproved && (
            <Typography variant="body2" gutterBottom>
              Compare with the live version v{latestApproved.version} above before approving. The new prices apply from 00:00 London on {review.version.start_date}.
            </Typography>
          )}
          <TextField fullWidth multiline rows={2} margin="dense" label={review?.action === "reject" ? "Reason (required)" : "Comment"} value={comment} onChange={(e) => setComment(e.target.value)} />
          {review?.action === "approve" && (
            <>
              <Divider style={{ margin: "12px 0" }} />
              <TextField fullWidth margin="dense" label="MID override reason" value={override} onChange={(e) => setOverride(e.target.value)}
                helperText="Only needed if some MIDs are not validated yet. It is logged." />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReview(null)}>Cancel</Button>
          <Button color="primary" variant="contained" disabled={review?.action === "reject" ? !comment.trim() : !check || check.errors.length > 0} onClick={submitReview}>
            {review?.action === "approve" ? "Approve" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog disableBackdropClick open={header !== null} onClose={() => setHeader(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit campaign</DialogTitle>
        <DialogContent>
          {header && (
            <Grid container spacing={2}>
              <Grid item xs={12}><TextField fullWidth label="Name" value={header.name} onChange={(e) => setHeader({ ...header, name: e.target.value })} /></Grid>
              <Grid item xs={12}><TextField fullWidth label="Partner campaign ID" value={header.partnerCampaignRef} onChange={(e) => setHeader({ ...header, partnerCampaignRef: e.target.value })} helperText="The retailer's own campaign ID, e.g. eBay Partner Network" inputProps={{ maxLength: 64 }} /></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHeader(null)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={saveHeader}>Save</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CampaignDetail;
